import React, { useMemo } from 'react';
import { rootId } from 'shared/components/ui/virtualized-tree-list';
import { useToday } from 'shared/contexts/today';

import { useDeadlineTree } from '../../hooks/use-deadline-tree';
import { GroupedListProps } from '../../types';
import { MultipleDeadlinesGroupTaskList } from './multiple-deadlines-group-task-list';
import { SingleDeadlineGroupTaskList } from './single-deadline-group-task-list';

// todo: extract the spitsing of groups to a new grouping specifically for today

export const DeadlineGroupingTasksList: React.FC<GroupedListProps> = ({
  tasks,
  sortingMode,
  onReorder,
  customOrder = {},
  collapsedSections = [],
  onSectionCollapse,
  weekStartsOn,
  scrollElement,
  denseFormat,
  groupSettings,
  filterDocument,
  isContextMenuOpen,
  defaultNewTaskDate,
}) => {
  const today = useToday();

  /*
   * We need to find the effective end-date of the "Date" filter to clamp Habits and Repeating Tasks.
   * If they are scheduled for "Today" (via Date filter), they shouldn't show up in future Deadline buckets.
   */
  const dateFilterEndDate = useMemo(() => {
    // 1. Check direct Date filter
    const directDateFilter = filterDocument?.filters?.date;
    if (
      directDateFilter?.type === 'custom' &&
      directDateFilter.custom?.type === 'before'
    ) {
      return directDateFilter.custom.end;
    }

    // 2. Check OR branches for Date filter
    const orBranches = filterDocument?.or ?? [];
    for (const branch of orBranches) {
      const branchDateFilter = branch.date;
      if (
        branchDateFilter?.type === 'custom' &&
        branchDateFilter.custom?.type === 'before'
      ) {
        return branchDateFilter.custom.end;
      }
    }

    return undefined;
  }, [filterDocument]);

  const treeData = useDeadlineTree({
    tasks,
    customOrder,
    sortingMode,
    today,
    weekStartsOn,
    showEmptyOverdue:
      groupSettings?.showEmptyGroups || groupSettings.showEmptyOverdue,
    showEmptyNoDate:
      groupSettings?.showEmptyGroups || groupSettings.showEmptyNoDate,
    showEmptyDate:
      groupSettings?.showEmptyGroups || groupSettings.showEmptyDate,
    dateFilterEndDate,
    hideNewTaskRow: groupSettings.hideNewTaskRow,
  });

  if (
    groupSettings.showSingleDeadlineAsNoGrouping &&
    treeData[rootId].children?.length === 1
  ) {
    return (
      <SingleDeadlineGroupTaskList
        treeData={treeData}
        tasks={tasks}
        onReorder={onReorder}
        weekStartsOn={weekStartsOn}
        collapsedSections={collapsedSections}
        onSectionCollapse={onSectionCollapse}
        scrollElement={scrollElement}
        denseFormat={denseFormat}
        isContextMenuOpen={isContextMenuOpen}
        defaultNewTaskDate={defaultNewTaskDate}
        goalId={groupSettings.goalId}
        lifeAreaId={groupSettings.lifeAreaId}
        tagId={groupSettings.tagId}
        displayDue={groupSettings.displayDue}
        showRepeatingAsTodo={groupSettings.showRepeatingAsTodo}
      />
    );
  }

  return (
    <MultipleDeadlinesGroupTaskList
      treeData={treeData}
      tasks={tasks}
      onReorder={onReorder}
      weekStartsOn={weekStartsOn}
      collapsedSections={collapsedSections}
      onSectionCollapse={onSectionCollapse}
      scrollElement={scrollElement}
      denseFormat={denseFormat}
      isContextMenuOpen={isContextMenuOpen}
      defaultNewTaskDate={defaultNewTaskDate}
      goalId={groupSettings.goalId}
      lifeAreaId={groupSettings.lifeAreaId}
      tagId={groupSettings.tagId}
      displayDue={groupSettings.displayDue}
      showRepeatingAsTodo={groupSettings.showRepeatingAsTodo}
    />
  );
};
